import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import { HttpError } from '@airship-platform/http-service';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { CustomerMedia } from './appointmentProposalFiles.models';
import {
  initialState,
  proposalBuilderSurveyOptionsReducer,
} from './appointmentProposalFiles.reducer';

interface AppointmentProposalFilesContextProps {
  files: CustomerMedia[];
  isLoading: boolean;
  isLoaded: boolean;
  isProcessing: string[];
  failedUploads: string[];
  uploadProgress: Record<string, number>;
  fetchAppointmentFiles: () => void;
  uploadAppointmentFiles: (
    files: File[],
    uploadCategory?: 'PRESENTATION_HEADER' | 'EXISTING_SYSTEM'
  ) => Promise<string[]>;
  deleteCustomerFile: (customerFileId: string) => void;
  updateCustomerFile: (
    customerFileId: string,
    title: string,
    notes: string
  ) => void;
  generateProposalOptionWorkOrder: (
    appointmentProposalId: string
  ) => Promise<CustomerMedia[]>;
}

const AppointmentProposalFilesContext = createContext<
  AppointmentProposalFilesContextProps | undefined
>(undefined);

export const AppointmentProposalFilesProvider: React.FC<
  PropsWithChildren<{ customerId: string; appointmentId: string }>
> = ({ children, customerId, appointmentId }) => {
  const [state, dispatch] = useReducer(
    proposalBuilderSurveyOptionsReducer,
    initialState
  );
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(`Error fetching files: ${state.error}`, 'error');
  }, [showToast, state.error]);

  const files = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter((a) => a.metadata.appointmentId === state.currentAppointmentId),
    [state.entities, state.ids, state.currentAppointmentId]
  );

  const fetchAppointmentFiles: AppointmentProposalFilesContextProps['fetchAppointmentFiles'] =
    useCallback(async () => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }
      dispatch({ type: 'FETCH_FILES', payload: { appointmentId } });
      try {
        const files = await httpClient.get<CustomerMedia[]>(
          `/customers/${customerId}/files?appointmentId=${appointmentId}`
        );
        dispatch({ type: 'FETCH_FILES_SUCCESS', payload: { files } });
      } catch (error) {
        dispatch({
          type: 'FETCH_FILES_FAILURE',
          payload: { error: `${error}` },
        });
      }
    }, [appointmentId, customerId, httpClient, isOnline]);

  const uploadAppointmentFiles: AppointmentProposalFilesContextProps['uploadAppointmentFiles'] =
    useCallback(
      async (
        files: File[],
        uploadCategory?: 'PRESENTATION_HEADER' | 'EXISTING_SYSTEM'
      ) => {
        const res = await Promise.allSettled(
          files.map(async (file) => {
            const metadataResponse = await httpClient.post<{
              preSignedUrl: string;
              fileId: string;
              fileKey: string;
            }>(`/appointments/${appointmentId}/upload`, {
              filename: file.name,
              contentType: file.type,
              uploadCategory,
            });

            const abort = new AbortController();
            dispatch({
              type: 'UPLOAD_FILE',
              payload: {
                file: {
                  customerFileId: metadataResponse.fileId,
                  filename: file.name,
                  url: URL.createObjectURL(file),
                  contentType: file.type,
                  customerId,
                  metadata: { appointmentId, uploadCategory },
                  title: '',
                  notes: '',
                  uploadAbort: abort,
                },
              },
            });

            await httpClient
              .put(metadataResponse.preSignedUrl, file, {
                headers: { Authorization: undefined },
                signal: abort.signal,
                onUploadProgress: (upload) =>
                  dispatch({
                    type: 'UPLOAD_FILE_PROGRESS',
                    payload: {
                      fileId: metadataResponse.fileId,
                      progress: upload.progress
                        ? Math.round(upload.progress * 100)
                        : 100,
                    },
                  }),
              })
              .catch((error) => {
                if (
                  error instanceof HttpError &&
                  error.message === 'canceled'
                ) {
                  dispatch({
                    type: 'UPLOAD_FILE_CANCEL',
                    payload: { fileId: metadataResponse.fileId },
                  });
                  return;
                }
                dispatch({
                  type: 'UPLOAD_FILE_ERROR',
                  payload: { fileId: metadataResponse.fileId },
                });
              });

            dispatch({
              type: 'UPLOAD_FILE_SUCCESS',
              payload: { fileId: metadataResponse.fileId },
            });

            return metadataResponse.fileId;
          })
        );

        const errors = res.flatMap((r) =>
          r.status === 'rejected' ? [r.reason || 'unknown error'] : []
        );

        if (errors.length) {
          dispatch({
            type: 'UPLOAD_FILE_FAILURE',
            payload: { error: errors.join(', ') },
          });
        }

        return res.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
      },
      [appointmentId, customerId, httpClient]
    );

  const deleteCustomerFile: AppointmentProposalFilesContextProps['deleteCustomerFile'] =
    useCallback(
      async (customerFileId: string) => {
        dispatch({ type: 'DELETE_FILE', payload: { fileId: customerFileId } });
        try {
          await httpClient.delete(
            `/customers/${customerId}/file/${customerFileId}`
          );
          dispatch({
            type: 'DELETE_FILE_SUCCESS',
            payload: { fileId: customerFileId },
          });
        } catch (error) {
          dispatch({
            type: 'DELETE_FILE_FAILURE',
            payload: { error: `${error}` },
          });
        }
      },
      [customerId, httpClient]
    );

  const updateCustomerFile: AppointmentProposalFilesContextProps['updateCustomerFile'] =
    useCallback(
      async (customerFileId: string, title: string, notes: string) => {
        dispatch({ type: 'UPDATE_FILE', payload: { fileId: customerFileId } });
        try {
          const file = await httpClient.put<CustomerMedia>(
            `/customers/${customerId}/file/${customerFileId}`,
            { title, notes }
          );
          dispatch({ type: 'UPDATE_FILE_SUCCESS', payload: { file } });
        } catch (error) {
          dispatch({
            type: 'UPDATE_FILE_FAILURE',
            payload: { error: `${error}` },
          });
        }
      },
      [customerId, httpClient]
    );

  const generateProposalOptionWorkOrder: AppointmentProposalFilesContextProps['generateProposalOptionWorkOrder'] =
    useCallback(
      async (appointmentProposalId: string) => {
        dispatch({
          type: 'GENERATE_PROPOSAL_OPTION_WORK_ORDER',
        });
        try {
          const files = await httpClient.post<CustomerMedia[]>(
            `/proposal-pdfs/${appointmentId}/proposal/${appointmentProposalId}/work-order`
          );
          dispatch({
            type: 'GENERATE_PROPOSAL_OPTION_WORK_ORDER_SUCCESS',
            payload: {
              files,
            },
          });
          return files;
        } catch (error) {
          dispatch({
            type: 'GENERATE_PROPOSAL_OPTION_WORK_ORDER_FAILURE',
            payload: {
              error: `${error}`,
            },
          });
          throw error;
        }
      },
      [httpClient, appointmentId]
    );

  return (
    <AppointmentProposalFilesContext.Provider
      value={{
        files,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        isProcessing: state.isProcessing,
        failedUploads: state.failedUploads,
        uploadProgress: state.uploads,
        fetchAppointmentFiles,
        uploadAppointmentFiles,
        deleteCustomerFile,
        updateCustomerFile,
        generateProposalOptionWorkOrder,
      }}
    >
      {children}
    </AppointmentProposalFilesContext.Provider>
  );
};

export const useAppointmentProposalFiles = () => {
  const context = useContext(AppointmentProposalFilesContext);
  if (!context) {
    throw new Error(
      'useAppointmentProposalFiles must be used within a AppointmentProposalFilesProvider'
    );
  }
  return context;
};
